// Utility functions for managing offers on NFT pieces
// Handles offer creation, acceptance, cancellation, and updates

import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import { 
  getAssociatedTokenAddress, 
  createApproveInstruction,
  createRevokeInstruction,
  TOKEN_PROGRAM_ID
} from '@solana/spl-token';
import { supabase } from '../lib/supabase';

// Platform wallet for delegate-based transfers
export const PLATFORM_WALLET_ADDRESS = '8cm597KjGmwTSzadrcVCHVpqwzdRZy1BM2yUrDDEpJcB';
const PLATFORM_DELEGATE = new PublicKey(PLATFORM_WALLET_ADDRESS);

// Helper to get RPC connection
const getConnection = () => {
  const rpcUrl = import.meta.env.VITE_SOLANA_RPC_URL || 'https://api.devnet.solana.com';
  return new Connection(rpcUrl, 'confirmed');
};

/**
 * Create a new offer on a piece
 * @param {Object} wallet - Connected wallet
 * @param {Object} offerData - Offer details
 * @returns {Promise<Object>} Result with offerId
 */
export const createOffer = async (wallet, offerData) => {
  try {
    console.log('📝 Creating offer...');
    console.log('Offerer:', wallet.publicKey.toString());
    console.log('Piece:', offerData.pieceName);
    console.log('Offer Price:', offerData.offerPrice, 'SOL');

    // Validate inputs
    if (!offerData.pieceId || !offerData.mintAddress) {
      throw new Error('Piece ID and mint address are required');
    }

    if (!offerData.offerPrice || offerData.offerPrice <= 0) {
      throw new Error('Offer price must be greater than 0');
    }

    // Call edge function to create offer
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/create-offer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        offererWallet: wallet.publicKey.toString(),
        pieceId: offerData.pieceId,
        mintAddress: offerData.mintAddress,
        offerPrice: offerData.offerPrice,
        message: offerData.message || '',
        expirationDays: offerData.expirationDays || 7,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge function error response:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || `Error: ${response.status}`);
      } catch (parseError) {
        throw new Error(`Error (${response.status}): ${errorText.substring(0, 200)}`);
      }
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to create offer');
    }

    console.log('✅ Offer created successfully!');
    console.log('Offer ID:', result.offer.id);

    return {
      success: true,
      offer: result.offer,
      message: result.message,
    };
  } catch (error) {
    console.error('Error creating offer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Accept an offer and prepare for execution
 * Owner must first set delegate approval, then call this function
 * @param {Object} wallet - Connected wallet (piece owner)
 * @param {string} offerId - Offer UUID
 * @returns {Promise<Object>} Result with transaction details
 */
export const acceptOffer = async (wallet, offerId) => {
  try {
    console.log('✅ Accepting offer:', offerId);
    console.log('Owner:', wallet.publicKey.toString());

    // Get offer details first
    const { data: offer, error: offerError } = await supabase
      .from('offers')
      .select('*')
      .eq('id', offerId)
      .maybeSingle();

    if (offerError || !offer) {
      throw new Error(`Offer not found: ${offerError?.message || 'Unknown error'}`);
    }

    // Verify user is the owner
    if (offer.piece_owner_wallet !== wallet.publicKey.toString()) {
      throw new Error('You are not the owner of this piece');
    }

    // Step 1: Set delegate approval for the NFT
    console.log('🔑 Setting delegate approval for NFT transfer...');
    
    const connection = getConnection();
    const mintPubkey = new PublicKey(offer.mint_address);
    const tokenAccount = await getAssociatedTokenAddress(
      mintPubkey,
      wallet.publicKey,
      true
    );

    console.log('🔍 Checking token account:', tokenAccount.toString());
    
    // Verify the token account exists
    const tokenAccountInfo = await connection.getAccountInfo(tokenAccount);
    if (!tokenAccountInfo) {
      throw new Error('Token account does not exist. The NFT may not be minted yet or is not in your wallet.');
    }
    
    console.log('✅ Token account exists, balance:', tokenAccountInfo.lamports);

    // Create delegate approval transaction
    const transaction = new Transaction();
    transaction.add(
      createApproveInstruction(
        tokenAccount, // token account
        PLATFORM_DELEGATE, // delegate
        wallet.publicKey, // owner
        1 // amount (NFTs are always 1)
      )
    );

    // Get recent blockhash
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    transaction.feePayer = wallet.publicKey;

    // Send and confirm transaction
    console.log('📤 Sending delegate approval transaction...');
    const txResult = await wallet.sendTransaction(transaction, connection);
    const signature = typeof txResult === 'string' ? txResult : (txResult?.signature || txResult?.txid || txResult?.transactionSignature || txResult);
    
    console.log('⏳ Confirming transaction...');
    await connection.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    });

    console.log('✅ Delegate approval confirmed:', signature);

    // Step 2: Call edge function to accept offer and prepare purchase transaction
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/accept-offer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        offerId,
        ownerWallet: wallet.publicKey.toString(),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge function error response:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || `Error: ${response.status}`);
      } catch (parseError) {
        throw new Error(`Error (${response.status}): ${errorText.substring(0, 200)}`);
      }
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to accept offer');
    }

    console.log('✅ Offer accepted successfully!');
    console.log('Transaction prepared for buyer to execute');

    return {
      success: true,
      offerId: result.offerId,
      transaction: result.transaction,
      details: result.details,
      message: result.message,
    };
  } catch (error) {
    console.error('Error accepting offer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Complete purchase for an accepted offer (buyer executes the transaction)
 * @param {Object} wallet - Connected wallet (must be offerer/buyer)
 * @param {string} offerId - Offer UUID
 * @returns {Promise<Object>} Result with transaction signature
 */
export const completePurchase = async (wallet, offerId) => {
  try {
    console.log('💰 Completing purchase for offer:', offerId);
    console.log('Buyer:', wallet.publicKey.toString());

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Get offer details to verify
    const { data: offer, error: offerError } = await supabase
      .from('offers')
      .select('*')
      .eq('id', offerId)
      .maybeSingle();

    if (offerError || !offer) {
      throw new Error(`Offer not found: ${offerError?.message || 'Unknown error'}`);
    }

    // Verify user is the buyer
    if (offer.offerer_wallet !== wallet.publicKey.toString()) {
      throw new Error('You are not the buyer of this offer');
    }

    // Verify offer is accepted
    if (offer.status !== 'accepted') {
      throw new Error(`Offer must be accepted first. Current status: ${offer.status}`);
    }

    console.log('✅ Offer verified, preparing to execute purchase');
    console.log('   Piece:', offer.piece_name);
    console.log('   Price:', offer.offer_price, 'SOL');
    console.log('   Seller:', offer.piece_owner_wallet);

    // Call edge function to get/regenerate the transaction
    // The seller already approved delegate, so we just need buyer to sign and pay
    const response = await fetch(`${supabaseUrl}/functions/v1/accept-offer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        offerId,
        ownerWallet: offer.piece_owner_wallet,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge function error:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || `Error: ${response.status}`);
      } catch (parseError) {
        throw new Error(`Error (${response.status}): ${errorText.substring(0, 200)}`);
      }
    }

    const result = await response.json();

    if (!result.success || !result.transaction) {
      throw new Error(result.error || 'Failed to prepare transaction');
    }

    console.log('📝 Transaction received from server, buyer signing...');

    // Deserialize the transaction
    const { Transaction } = await import('@solana/web3.js');
    const transactionBuffer = Buffer.from(result.transaction, 'base64');
    const transaction = Transaction.from(transactionBuffer);

    // Connect to Solana
    const connection = getConnection();

    // Check if the blockhash is still valid
    console.log('🔍 Checking if transaction blockhash is still valid...');
    try {
      const isBlockhashValid = await connection.isBlockhashValid(transaction.recentBlockhash);
      if (!isBlockhashValid.value) {
        console.warn('⚠️ Transaction blockhash has expired. Requesting new transaction from server...');
        throw new Error('Transaction has expired. Please try again.');
      }
      console.log('✅ Transaction blockhash is valid');
    } catch (blockcheckError) {
      if (blockcheckError.message.includes('expired')) {
        throw blockcheckError;
      }
      console.warn('⚠️ Could not verify blockhash validity:', blockcheckError.message);
      // Continue anyway if we can't check
    }

    // Send transaction (wallet will sign it)
    console.log('📤 Sending transaction for buyer signature and submission...');
    console.log('Transaction details:', {
      recentBlockhash: transaction.recentBlockhash,
      signatures: transaction.signatures.length,
      feePayer: transaction.feePayer?.toString(),
    });
    
    let txResult;
    try {
      // Check if wallet has enough SOL for the transaction
      const balance = await connection.getBalance(wallet.publicKey);
      const balanceSOL = balance / 1000000000;
      console.log(`💰 Wallet balance: ${balanceSOL} SOL`);
      console.log(`💸 Transaction cost: ${offer.offer_price} SOL + fees`);
      
      if (balanceSOL < parseFloat(offer.offer_price) + 0.01) {
        throw new Error(`Insufficient balance. You have ${balanceSOL.toFixed(4)} SOL but need at least ${(parseFloat(offer.offer_price) + 0.01).toFixed(4)} SOL (including network fees)`);
      }

      // Try to simulate the transaction first
      console.log('🧪 Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        console.log('Simulation result:', simulation);
        
        if (simulation.value.err) {
          console.error('❌ Transaction simulation failed:', simulation.value.err);
          console.error('Simulation logs:', simulation.value.logs);
          
          // Parse the error
          const errorObj = simulation.value.err;
          if (errorObj.InstructionError) {
            const [instructionIndex, error] = errorObj.InstructionError;
            console.error(`Instruction ${instructionIndex} failed with error:`, error);
            
            // Check if it's a Custom error 1 (insufficient funds or delegate authority)
            if (error.Custom === 1) {
              throw new Error('The seller\'s delegate approval has expired or is invalid. Please ask the seller to accept the offer again.');
            }
          }
          
          throw new Error(`Transaction would fail: ${JSON.stringify(simulation.value.err)}`);
        }
        console.log('✅ Transaction simulation successful');
      } catch (simError) {
        console.error('⚠️ Simulation error:', simError);
        
        // If this is our custom error about delegate, throw it
        if (simError.message?.includes('delegate approval')) {
          throw simError;
        }
        
        // Otherwise just log and continue
        console.log('Continuing despite simulation error...');
      }

      txResult = await wallet.sendTransaction(transaction, connection);
    } catch (sendError) {
      console.error('❌ Failed to send transaction:', sendError);
      console.error('Error details:', {
        message: sendError.message,
        code: sendError.code,
        logs: sendError.logs,
        name: sendError.name,
        stack: sendError.stack,
      });
      
      // Try to extract more meaningful error
      let errorMessage = sendError.message || 'Unknown wallet error';
      if (sendError.message?.includes('insufficient')) {
        errorMessage = `Insufficient funds. Please ensure you have enough SOL to cover the purchase price (${offer.offer_price} SOL) plus network fees (~0.01 SOL)`;
      } else if (sendError.message?.includes('blockhash')) {
        errorMessage = 'Transaction has expired. Please try again.';
      } else if (sendError.logs && sendError.logs.length > 0) {
        errorMessage = `Transaction failed: ${sendError.logs.join(', ')}`;
      }
      
      throw new Error(`Failed to send transaction: ${errorMessage}`);
    }
    
    const signature = typeof txResult === 'string' ? txResult : (txResult?.signature || txResult?.txid || txResult?.transactionSignature || txResult);
    
    if (!signature) {
      console.error('❌ No signature received from wallet');
      throw new Error('Transaction was sent but no signature was returned');
    }
    
    console.log('⏳ Confirming transaction:', signature);
    const confirmation = await connection.confirmTransaction(signature, 'confirmed');

    if (confirmation.value.err) {
      console.error('❌ Transaction confirmation failed:', confirmation.value.err);
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
    }

    console.log('✅ Purchase completed! Transaction:', signature);

    // Update offer status to executed
    console.log('📝 Updating offer status to executed...');
    const { data: updateData, error: offerUpdateError } = await supabase
      .from('offers')
      .update({
        status: 'executed',
        executed_at: new Date().toISOString(),
        execution_signature: signature,
      })
      .eq('id', offerId)
      .select();

    if (offerUpdateError) {
      console.error('❌ Failed to update offer status:', offerUpdateError);
      throw new Error(`Failed to update offer status: ${offerUpdateError.message}`);
    }
    
    console.log('✅ Offer status update response:', updateData);
    
    // Verify the update succeeded by fetching the offer again
    const { data: verifyData, error: verifyError } = await supabase
      .from('offers')
      .select('status')
      .eq('id', offerId)
      .single();
    
    if (verifyError) {
      console.error('❌ Failed to verify offer update:', verifyError);
    } else {
      console.log('✅ Verified offer status in database:', verifyData.status);
      if (verifyData.status !== 'executed') {
        console.error('⚠️ WARNING: Offer status mismatch! Expected "executed" but got:', verifyData.status);
      }
    }

    // STEP 1: Close ALL active listings for this mint (stale listing cleanup)
    console.log('🔧 Closing all active listings for mint:', offer.mint_address);
    const { data: closedListings, error: listingUpdateError } = await supabase
      .from('listings')
      .update({
        status: 'filled',
        buyer_wallet: wallet.publicKey.toString(),
        filled_at: new Date().toISOString(),
      })
      .eq('mint_address', offer.mint_address)
      .eq('status', 'active')
      .select('id');

    if (listingUpdateError) {
      console.error('❌ Failed to close active listings:', listingUpdateError);
      // Don't throw, this is not critical
    } else if (closedListings && closedListings.length > 0) {
      console.log(`✅ Closed ${closedListings.length} active listing(s):`, closedListings.map(l => l.id));
    } else {
      console.log('ℹ️ No active listings found to close');
    }

    // STEP 2: Update piece to clean, unlisted state with new owner
    console.log('📝 Updating piece ownership...');
    const { error: pieceUpdateError } = await supabase
      .from('pieces')
      .update({
        holder_wallet: wallet.publicKey.toString(),
        listing_status: null,  // Clear status - buyer can now list it fresh
        listing_price: null,  // Clear the listing price
        is_delegate_active: false,  // Clear delegate
        listing_delegate: null,  // Clear delegate wallet
        seller_ata: null,  // Clear seller ATA
        delegate_expires_at: null,  // Clear expiration
        sold_at: new Date().toISOString(),
        last_sale_price_lamports: Math.round(parseFloat(offer.offer_price) * 1_000_000_000), // Convert SOL to lamports
        updated_at: new Date().toISOString(),
      })
      .eq('mint_address', offer.mint_address);

    if (pieceUpdateError) {
      console.error('❌ Failed to update piece ownership:', pieceUpdateError);
      // Don't throw, this is not critical
    } else {
      console.log('✅ Piece ownership updated to clean state');
    }

    // Cancel any other active offers on this piece
    console.log('📝 Cancelling other active offers...');
    const { error: otherOffersError } = await supabase
      .from('offers')
      .update({
        status: 'cancelled',
      })
      .eq('mint_address', offer.mint_address)
      .neq('id', offerId)
      .in('status', ['active', 'accepted']);

    if (otherOffersError) {
      console.error('❌ Failed to cancel other offers:', otherOffersError);
      // Don't throw, this is not critical
    } else {
      console.log('✅ Other offers cancelled');
    }

    // Log activity
    await supabase.from('offer_activity').insert({
      offer_id: offerId,
      activity_type: 'executed',
      wallet: wallet.publicKey.toString(),
      transaction_signature: signature,
      metadata: {
        offer_price: offer.offer_price,
        piece_name: offer.piece_name,
        seller: offer.piece_owner_wallet,
      },
    });

    // Log to wallet activity
    await supabase.from('wallet_activity').insert([
      {
        wallet: wallet.publicKey.toString(),
        activity_type: 'piece_purchased_via_offer',
        metadata: {
          offer_id: offerId,
          mint_address: offer.mint_address,
          piece_name: offer.piece_name,
          price: offer.offer_price,
          seller: offer.piece_owner_wallet,
          transaction_signature: signature,
        },
      },
      {
        wallet: offer.piece_owner_wallet,
        activity_type: 'piece_sold_via_offer',
        metadata: {
          offer_id: offerId,
          mint_address: offer.mint_address,
          piece_name: offer.piece_name,
          price: offer.offer_price,
          buyer: wallet.publicKey.toString(),
          transaction_signature: signature,
        },
      },
    ]);

    // Log to nft_activity table (for Activity page and modal activity tabs)
    const now = new Date();
    const timestamp = Math.floor(now.getTime() / 1000); // Unix timestamp in seconds
    const priceLamports = Math.floor(parseFloat(offer.offer_price) * 1e9); // Convert SOL to lamports
    
    const activityRecord = {
      mint_address: offer.mint_address,
      event_type: 'sale',
      from_address: offer.piece_owner_wallet,
      to_address: wallet.publicKey.toString(),
      price: parseFloat(offer.offer_price), // Price in SOL
      amount_lamports: priceLamports,
      signature: signature,
      tx_sig: signature,
      transaction_hash: signature,
      event_timestamp: timestamp,
      block_time: now.toISOString(),
      created_at: now.toISOString(),
      source: 'app_offer',
      slot: 0
    };
    
    console.log('📝 Logging sale activity to nft_activity table:', activityRecord);
    
    const { data: insertedActivity, error: activityError } = await supabase.from('nft_activity').insert(activityRecord).select();
    
    if (activityError) {
      console.error('❌ Failed to log sale activity:', activityError);
      console.error('Activity data that failed to insert:', activityRecord);
    } else {
      console.log('✅ Sale activity logged to nft_activity successfully:', insertedActivity);
    }

    console.log('✅ Purchase completed successfully!');
    console.log('   - Ownership transferred');
    console.log('   - Listings removed');
    console.log('   - Other offers cancelled');

    return {
      success: true,
      signature,
      offerId,
      message: `Purchase completed! You bought ${offer.piece_name} for ${offer.offer_price} SOL`,
    };
  } catch (error) {
    console.error('Error completing purchase:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Cancel an offer (only offerer can cancel)
 * @param {Object} wallet - Connected wallet (must be offerer)
 * @param {string} offerId - Offer UUID
 * @param {string} reason - Cancellation reason (optional)
 * @returns {Promise<Object>} Result
 */
export const cancelOffer = async (wallet, offerId, reason = '') => {
  try {
    console.log('❌ Cancelling offer:', offerId);

    // Get offer details
    const { data: offer, error: offerError } = await supabase
      .from('offers')
      .select('*')
      .eq('id', offerId)
      .maybeSingle();

    if (offerError || !offer) {
      throw new Error(`Offer not found: ${offerError?.message}`);
    }

    // Verify user is the offerer
    if (offer.offerer_wallet !== wallet.publicKey.toString()) {
      throw new Error('Only the offerer can cancel this offer');
    }

    // Verify offer is active
    if (offer.status !== 'active') {
      throw new Error(`Cannot cancel offer with status: ${offer.status}`);
    }

    // Update offer status
    const { error: updateError } = await supabase
      .from('offers')
      .update({
        status: 'cancelled',
        responded_at: new Date().toISOString(),
        response_message: reason,
      })
      .eq('id', offerId);

    if (updateError) {
      throw new Error(`Failed to cancel offer: ${updateError.message}`);
    }

    // Log activity
    await supabase.from('offer_activity').insert({
      offer_id: offerId,
      activity_type: 'cancelled',
      wallet: wallet.publicKey.toString(),
      metadata: {
        reason,
        piece_name: offer.piece_name,
      },
    });

    console.log('✅ Offer cancelled');

    return {
      success: true,
      offerId,
      message: 'Offer cancelled successfully',
    };
  } catch (error) {
    console.error('Error cancelling offer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Decline an offer (only owner can decline)
 * @param {Object} wallet - Connected wallet (must be piece owner)
 * @param {string} offerId - Offer UUID
 * @param {string} reason - Decline reason (optional)
 * @returns {Promise<Object>} Result
 */
export const declineOffer = async (wallet, offerId, reason = '') => {
  try {
    console.log('❌ Declining offer:', offerId);

    // Get offer details
    const { data: offer, error: offerError } = await supabase
      .from('offers')
      .select('*')
      .eq('id', offerId)
      .maybeSingle();

    if (offerError || !offer) {
      throw new Error(`Offer not found: ${offerError?.message}`);
    }

    // Verify user is the owner
    if (offer.piece_owner_wallet !== wallet.publicKey.toString()) {
      throw new Error('Only the piece owner can decline this offer');
    }

    // Verify offer is active
    if (offer.status !== 'active') {
      throw new Error(`Cannot decline offer with status: ${offer.status}`);
    }

    // Update offer status
    const { error: updateError } = await supabase
      .from('offers')
      .update({
        status: 'declined',
        responded_at: new Date().toISOString(),
        response_message: reason,
      })
      .eq('id', offerId);

    if (updateError) {
      throw new Error(`Failed to decline offer: ${updateError.message}`);
    }

    // Log activity
    await supabase.from('offer_activity').insert({
      offer_id: offerId,
      activity_type: 'declined',
      wallet: wallet.publicKey.toString(),
      metadata: {
        reason,
        piece_name: offer.piece_name,
      },
    });

    console.log('✅ Offer declined');

    return {
      success: true,
      offerId,
      message: 'Offer declined successfully',
    };
  } catch (error) {
    console.error('Error declining offer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update an existing offer (only offerer can update, only active offers)
 * @param {Object} wallet - Connected wallet (must be offerer)
 * @param {string} offerId - Offer UUID
 * @param {number} newPrice - New offer price in SOL
 * @param {string} newMessage - New message (optional)
 * @returns {Promise<Object>} Result
 */
export const updateOffer = async (wallet, offerId, newPrice, newMessage = null) => {
  try {
    console.log('✏️ Updating offer:', offerId);
    console.log('New price:', newPrice, 'SOL');

    if (newPrice <= 0) {
      throw new Error('Offer price must be greater than 0');
    }

    // Get offer details
    const { data: offer, error: offerError } = await supabase
      .from('offers')
      .select('*')
      .eq('id', offerId)
      .maybeSingle();

    if (offerError || !offer) {
      throw new Error(`Offer not found: ${offerError?.message}`);
    }

    // Verify user is the offerer
    if (offer.offerer_wallet !== wallet.publicKey.toString()) {
      throw new Error('Only the offerer can update this offer');
    }

    // Verify offer is active
    if (offer.status !== 'active') {
      throw new Error(`Cannot update offer with status: ${offer.status}`);
    }

    // Update offer
    const updates = {
      offer_price: newPrice,
    };

    if (newMessage !== null) {
      updates.message = newMessage;
    }

    const { error: updateError } = await supabase
      .from('offers')
      .update(updates)
      .eq('id', offerId);

    if (updateError) {
      throw new Error(`Failed to update offer: ${updateError.message}`);
    }

    // Log activity
    await supabase.from('offer_activity').insert({
      offer_id: offerId,
      activity_type: 'updated',
      wallet: wallet.publicKey.toString(),
      metadata: {
        old_price: offer.offer_price,
        new_price: newPrice,
        piece_name: offer.piece_name,
      },
    });

    console.log('✅ Offer updated');

    return {
      success: true,
      offerId,
      newPrice,
      message: 'Offer updated successfully',
    };
  } catch (error) {
    console.error('Error updating offer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get all offers for a wallet (both made and received)
 * @param {string} walletAddress - Wallet address
 * @param {string} type - 'made' or 'received' or 'all'
 * @param {string} status - Optional status filter
 * @returns {Promise<Object>} Offers array
 */
export const getWalletOffers = async (walletAddress, type = 'all', status = null) => {
  try {
    console.log('🔍 Fetching offers for wallet:', walletAddress, 'type:', type, 'status:', status);
    
    // First, get basic offers
    let query = supabase
      .from('offers')
      .select('*');

    // Filter by type
    if (type === 'made') {
      query = query.eq('offerer_wallet', walletAddress);
    } else if (type === 'received') {
      query = query.eq('piece_owner_wallet', walletAddress);
    } else {
      query = query.or(`offerer_wallet.eq.${walletAddress},piece_owner_wallet.eq.${walletAddress}`);
    }

    // Filter by status
    if (status) {
      query = query.eq('status', status);
    }

    // Order by created date
    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('❌ Supabase query error:', error);
      throw new Error(error.message);
    }

    console.log('✅ Raw offers data from Supabase:', data);
    console.log('📊 Number of offers found:', data?.length || 0);

    // Transform data and enrich with piece/creator information
    const transformedData = await Promise.all((data || []).map(async (offer) => {
      let pieceData = null;
      let creatorProfile = null;
      
      // Fetch piece data if we have a piece_id
      if (offer.piece_id) {
        try {
          // First, fetch the complete piece data
          const { data: piece, error: pieceError } = await supabase
            .from('pieces')
            .select(`*`)
            .eq('id', offer.piece_id)
            .single();
          
          if (pieceError) {
            console.error(`⚠️ Error fetching piece ${offer.piece_id}:`, pieceError);
            console.error(`   Error details:`, JSON.stringify(pieceError, null, 2));
          } else if (piece) {
            pieceData = piece;
            console.log(`✅ Fetched piece for offer ${offer.id}:`, {
              pieceName: piece.name,
              creatorId: piece.creator_id,
              creatorWallet: piece.creator_wallet,
              description: piece.description,
              color: piece.color,
              brand: piece.brand,
              tags: piece.tags,
              price: piece.price,
              listing_price: piece.listing_price,
              quantity: piece.quantity,
              rarity: piece.rarity
            });
            
            // Now fetch the creator using creator_id
            if (piece.creator_id) {
              const { data: creator, error: creatorError } = await supabase
                .from('creators')
                .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created')
                .eq('id', piece.creator_id)
                .maybeSingle();
              
              if (creatorError) {
                console.error(`⚠️ Error fetching creator ${piece.creator_id}:`, creatorError);
              } else if (creator) {
                creatorProfile = {
                  username: creator.handle,
                  profile_picture_url: creator.profile_picture_url,
                  verified: creator.is_verified,
                  verificationType: creator.verification_type || 'none',
                  pieces_count: creator.total_pieces_created || 0,
                  wallet: creator.wallet
                };
                
                console.log(`✅ Fetched creator for offer ${offer.id}:`, {
                  creatorUsername: creator.handle,
                  creatorVerified: creator.is_verified,
                  piecesCount: creator.total_pieces_created || 0
                });
              } else {
                console.warn(`⚠️ No creator found with ID ${piece.creator_id}`);
              }
            } else {
              console.warn(`⚠️ Piece ${offer.piece_id} has no creator_id`);
            }
          }
        } catch (err) {
          console.error('❌ Exception fetching piece data for offer:', offer.id, err);
        }
      } else {
        console.warn(`⚠️ Offer ${offer.id} has no piece_id`);
      }
      
      // Format creator with both naming conventions for compatibility
      const formattedCreator = creatorProfile ? {
        username: creatorProfile.username,
        profile_picture_url: creatorProfile.profile_picture_url,
        profilePicture: creatorProfile.profile_picture_url,
        verified: creatorProfile.verified,
        verificationType: creatorProfile.verificationType || 'none',
        piecesCount: creatorProfile.pieces_count || 0,
        pieces_count: creatorProfile.pieces_count || 0,
        wallet: creatorProfile.wallet
      } : null;

      console.log(`🎨 Final formatted creator for offer ${offer.id}:`, formattedCreator);

      // Fetch offerer profile
      let offererProfile = null;
      if (offer.offerer_wallet) {
        try {
          const { data: offerer, error: offererError } = await supabase
            .from('creators')
            .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created')
            .eq('wallet', offer.offerer_wallet)
            .maybeSingle();
          
          if (!offererError && offerer) {
            offererProfile = {
              username: offerer.handle,
              profile_picture_url: offerer.profile_picture_url,
              profilePicture: offerer.profile_picture_url,
              verified: offerer.is_verified,
              verificationType: offerer.verification_type || 'none',
              piecesCount: offerer.total_pieces_created || 0,
              pieces_count: offerer.total_pieces_created || 0,
              wallet: offerer.wallet
            };
          }
        } catch (err) {
          console.error('❌ Error fetching offerer profile:', err);
        }
      }

      // Fetch piece owner profile
      let ownerProfile = null;
      if (offer.piece_owner_wallet) {
        try {
          const { data: owner, error: ownerError } = await supabase
            .from('creators')
            .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created')
            .eq('wallet', offer.piece_owner_wallet)
            .maybeSingle();
          
          if (!ownerError && owner) {
            ownerProfile = {
              username: owner.handle,
              profile_picture_url: owner.profile_picture_url,
              profilePicture: owner.profile_picture_url,
              verified: owner.is_verified,
              verificationType: owner.verification_type || 'none',
              piecesCount: owner.total_pieces_created || 0,
              pieces_count: owner.total_pieces_created || 0,
              wallet: owner.wallet
            };
          }
        } catch (err) {
          console.error('❌ Error fetching owner profile:', err);
        }
      }

      const transformedOffer = {
        ...offer,
        creator: formattedCreator,
        originalCreator: formattedCreator,
        offererProfile: offererProfile,
        ownerProfile: ownerProfile,
        // Image and model URLs
        piece_image_url: pieceData?.thumbnail_url || offer.piece_image_url,
        piece_thumbnail_url: pieceData?.thumbnail_url,
        piece_preview_url: pieceData?.preview_glb_url,
        piece_poster_url: pieceData?.poster_url,
        model_url: pieceData?.model_url,
        // Piece details
        piece_category: pieceData?.category || offer.category,
        piece_description: pieceData?.description,
        piece_brand: pieceData?.brand,
        piece_color: pieceData?.color,
        piece_rarity: pieceData?.rarity,
        piece_quantity: pieceData?.quantity,
        piece_total_made: pieceData?.total_made,
        piece_tags: pieceData?.tags,
        piece_price: pieceData?.price,
        piece_listing_price: pieceData?.listing_price,
        piece_created_at: pieceData?.created_at,
        // Royalty information
        royalty_bps: pieceData?.royalty_bps !== undefined ? pieceData.royalty_bps : null,
      };

      console.log(`📦 Transformed offer ${offer.id} piece data:`, {
        piece_description: transformedOffer.piece_description,
        piece_color: transformedOffer.piece_color,
        piece_tags: transformedOffer.piece_tags,
        piece_price: transformedOffer.piece_price,
        piece_listing_price: transformedOffer.piece_listing_price,
        royalty_bps: transformedOffer.royalty_bps
      });

      return transformedOffer;
    }));

    console.log('🎯 Final transformed offers count:', transformedData.length);

    return {
      success: true,
      offers: transformedData,
    };
  } catch (error) {
    console.error('Error getting wallet offers:', error);
    return {
      success: false,
      error: error.message,
      offers: [],
    };
  }
};

/**
 * Get offers for a specific piece
 * @param {string} mintAddress - Mint address of the piece
 * @param {string} status - Optional status filter (default: 'active')
 * @returns {Promise<Object>} Offers array
 */
export const getPieceOffers = async (mintAddress, status = 'active') => {
  try {
    let query = supabase
      .from('offers')
      .select('*')
      .eq('mint_address', mintAddress);

    if (status) {
      query = query.eq('status', status);
    }

    query = query.order('offer_price', { ascending: false });

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      offers: data || [],
    };
  } catch (error) {
    console.error('Error getting piece offers:', error);
    return {
      success: false,
      error: error.message,
      offers: [],
    };
  }
};

/**
 * Calculate time remaining for an offer
 * @param {string} expiresAt - ISO timestamp
 * @returns {string} Human-readable time remaining
 */
export const formatTimeRemaining = (expiresAt) => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry - now;
  
  if (diff <= 0) return 'Expired';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/**
 * Calculate expiration time
 * @param {number} days - Number of days from now
 * @returns {string} ISO timestamp
 */
export const calculateExpirationTime = (days = 7) => {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
};

/**
 * Get offer statistics for a piece
 * @param {string} mintAddress - Mint address
 * @returns {Promise<Object>} Statistics
 */
export const getPieceOfferStats = async (mintAddress) => {
  try {
    const { data, error } = await supabase
      .from('offer_stats_by_piece')
      .select('*')
      .eq('mint_address', mintAddress)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') { // Not found is ok
      throw new Error(error.message);
    }

    return {
      success: true,
      stats: data || {
        active_offer_count: 0,
        highest_offer: 0,
        lowest_offer: 0,
        avg_offer: 0,
      },
    };
  } catch (error) {
    console.error('Error getting piece offer stats:', error);
    return {
      success: false,
      error: error.message,
      stats: {
        active_offer_count: 0,
        highest_offer: 0,
        lowest_offer: 0,
        avg_offer: 0,
      },
    };
  }
};

/**
 * Check and auto-decline expired accepted offers (3-day deadline)
 * This function should be called periodically to clean up expired offers
 * @returns {Promise<Object>} Result with count of declined offers
 */
export const checkAndDeclineExpiredOffers = async () => {
  try {
    console.log('🔍 Checking for expired accepted offers...');
    
    // Query for accepted offers that have passed their 3-day deadline
    const { data: expiredOffers, error } = await supabase
      .from('offers')
      .select('id, piece_name, offerer_wallet, accepted_expires_at')
      .eq('status', 'accepted')
      .not('accepted_expires_at', 'is', null)
      .lt('accepted_expires_at', new Date().toISOString());

    if (error) {
      console.error('❌ Error checking expired offers:', error);
      throw new Error(error.message);
    }

    if (!expiredOffers || expiredOffers.length === 0) {
      console.log('✅ No expired accepted offers found');
      return {
        success: true,
        declined_count: 0,
        declined_offers: [],
      };
    }

    console.log(`⚠️ Found ${expiredOffers.length} expired accepted offer(s), declining...`);

    // Decline each expired offer
    const declineResults = [];
    for (const offer of expiredOffers) {
      try {
        const { error: updateError } = await supabase
          .from('offers')
          .update({
            status: 'declined',
            updated_at: new Date().toISOString(),
            response_message: 'Automatically declined - purchase not completed within 3 days',
          })
          .eq('id', offer.id);

        if (updateError) {
          console.error(`❌ Error declining offer ${offer.id}:`, updateError);
        } else {
          console.log(`✅ Auto-declined offer ${offer.id} for piece ${offer.piece_name}`);
          declineResults.push({
            id: offer.id,
            piece_name: offer.piece_name,
            offerer_wallet: offer.offerer_wallet,
          });

          // Log activity
          await supabase.from('offer_activity').insert({
            offer_id: offer.id,
            activity_type: 'declined',
            wallet: 'system', // System-triggered decline
            metadata: {
              reason: 'auto_declined_expired_deadline',
              piece_name: offer.piece_name,
              expired_at: offer.accepted_expires_at,
            },
          });
        }
      } catch (err) {
        console.error(`❌ Exception declining offer ${offer.id}:`, err);
      }
    }

    return {
      success: true,
      declined_count: declineResults.length,
      declined_offers: declineResults,
    };
  } catch (error) {
    console.error('❌ Error in checkAndDeclineExpiredOffers:', error);
    return {
      success: false,
      error: error.message,
      declined_count: 0,
      declined_offers: [],
    };
  }
};

